/**
 * API Utility Functions
 * Provides helper functions for making API requests with proper URL handling
 */

import { request } from '@umijs/max';
import { getApiUrl } from '@/config/api';
import { handleApiError, debugApiResponse, isApiRequestCanceled } from './errorHandler';

const REQUEST_OPTION_KEYS = new Set([
  'params',
  'data',
  'headers',
  'responseType',
  'timeout',
  'method',
  'withCredentials',
  'skipErrorHandler',
  'silent',
  'signal',
  'requestType',
]);

const isRequestOptionsObject = (options: any): boolean => {
  if (!options || typeof options !== 'object' || Array.isArray(options)) {
    return false;
  }

  return Object.keys(options).some((key) => REQUEST_OPTION_KEYS.has(key));
};

type IsAny<T> = 0 extends 1 & T ? true : false;

type IsApiEnvelope<T> = T extends object
  ? 'data' extends keyof T
    ? 'success' extends keyof T
      ? true
      : 'total' extends keyof T
        ? true
        : false
    : false
  : false;

type NormalizedApiData<T> =
  IsApiEnvelope<T> extends true ? (T extends { data?: infer Data } ? Data : T) : T;

export type NormalizedApiResponse<T> =
  IsAny<T> extends true
    ? any
    : T extends object
      ? IsApiEnvelope<T> extends true
        ? Omit<T, 'data' | 'success' | 'metadata'> & {
            data: NormalizedApiData<T>;
            success: boolean;
            message?: string;
            metadata?: any;
          }
        : {
            data: T;
            total?: number;
            success: boolean;
            message?: string;
            metadata?: any;
          }
      : {
          data: T;
          total?: number;
          success: boolean;
          message?: string;
          metadata?: any;
        };

/**
 * Normalize various backend response formats
 */
const normalizeResponse = <T = any>(response: any, endpoint: string): NormalizedApiResponse<T> => {
  // Log raw response for debugging
  console.log(`[API] Raw response for ${endpoint}:`, response);

  // Handle null/undefined responses
  if (!response) {
    debugApiResponse(endpoint, response);
    return {
      success: false,
      data: [] as any,
    } as NormalizedApiResponse<T>;
  }

  // If response already has success field, use it
  if ('success' in response && 'data' in response) {
    debugApiResponse(endpoint, response);
    return response;
  }

  // Handle paginated responses
  if ('data' in response && 'total' in response) {
    const result = {
      success: true,
      data: response.data,
      total: response.total,
      metadata: (response as any).metadata,
      ...(response.snapshot_at ? { snapshot_at: response.snapshot_at } : {}),
    };
    debugApiResponse(endpoint, result);
    return result as NormalizedApiResponse<T>;
  }

  // Handle simple data array responses
  if (Array.isArray(response)) {
    const result = {
      success: true,
      data: response as T,
      total: response.length,
    };
    debugApiResponse(endpoint, result);
    return result as NormalizedApiResponse<T>;
  }

  // Handle wrapped responses (e.g., { result: data })
  if ('result' in response) {
    return normalizeResponse(response.result, endpoint);
  }

  // Handle responses with items array
  if ('items' in response && Array.isArray(response.items)) {
    const result = {
      success: true,
      data: response.items as T,
      total: response.total || response.items.length,
      metadata: (response as any).metadata,
    };
    debugApiResponse(endpoint, result);
    return result as NormalizedApiResponse<T>;
  }

  // Default: treat entire response as data
  const result = {
    success: true,
    data: response as T,
  };
  debugApiResponse(endpoint, result);
  return result as NormalizedApiResponse<T>;
};

/**
 * Make an API request with automatic URL handling
 * This function handles both development (proxy) and production environments
 *
 * @param endpoint - API endpoint (e.g., '/api/products' or just 'products')
 * @param options - Request options (method, params, data, etc.)
 * @returns Promise with the API response
 */
export async function apiRequest<T = any>(
  endpoint: string,
  options?: any,
): Promise<NormalizedApiResponse<T>> {
  const url = getApiUrl(endpoint);

  // Debug logging for analytics requests
  if (endpoint.includes('analytics') && options?.params) {
    console.log(`[apiRequest] Full URL: ${url}`);
    console.log('[apiRequest] Request options:', options);
    if (options.params.locationIds !== undefined) {
      console.log('[apiRequest] locationIds in params:', options.params.locationIds);
    }
  }

  // Special logging for category-detail endpoint
  if (endpoint.includes('category-detail')) {
    console.log('[apiRequest] category-detail Full URL:', url);
    console.log('[apiRequest] category-detail options:', options);
    if (options?.params) {
      console.log('[apiRequest] category-detail params:', options.params);
      const queryString = new URLSearchParams(options.params).toString();
      console.log('[apiRequest] category-detail query string:', queryString);
    }
  }

  try {
    const response = await request<any>(url, options);

    // For blob responses, return the response directly without normalization
    if (options?.responseType === 'blob') {
      return { success: true, data: response as T } as NormalizedApiResponse<T>;
    }

    return normalizeResponse<T>(response, endpoint);
  } catch (error: any) {
    if (isApiRequestCanceled(error)) {
      if (process.env.NODE_ENV !== 'production') {
        console.debug(`[apiRequest] Request canceled for ${endpoint}`);
      }
      throw error;
    }
    // Respect silent/skip flags to avoid noisy UI for suggestion/background calls
    if (options?.skipErrorHandler || options?.silent) {
      throw error;
    }
    debugApiResponse(endpoint, null, error);

    // Check if this is a non-critical API call for brand/supplier/customer selectors
    const isNonCriticalApi =
      endpoint.includes('/api/brand') ||
      endpoint.includes('/api/mi-brands') ||
      endpoint.includes('/api/supplier') ||
      endpoint.includes('/api/customer');

    // Check if the error response actually contains valid data
    // This can happen when backend logs errors but still returns results
    const hasValidDataInError =
      error?.response?.data?.data && Array.isArray(error.response.data.data);

    // Special handling for product API that may return data even with error status
    const isProductApiWithData = endpoint.includes('/api/product') && hasValidDataInError;

    const duplicateBarcodeMessage =
      error?.response?.data?.message || error?.response?.data?.error || error?.message;
    const isDuplicateBarcodeError =
      endpoint.includes('/api/product') &&
      typeof duplicateBarcodeMessage === 'string' &&
      /barcode\s+[^\s]+\s+already exists/i.test(duplicateBarcodeMessage);

    // Don't show error messages for:
    // 1. 403 errors on non-critical APIs
    // 2. Product API calls that return valid data despite error status
    // 3. Duplicate barcode errors handled by the product form
    const shouldShowMessage = !(
      (error?.response?.status === 403 && isNonCriticalApi) ||
      isProductApiWithData ||
      isDuplicateBarcodeError
    );

    handleApiError(error, { showMessage: shouldShowMessage });
    throw error;
  }
}

/**
 * Helper functions for common HTTP methods
 */
export const api = {
  get: <T = any>(endpoint: string, options?: any, additionalOptions?: Record<string, unknown>) => {
    // Handle both params-only calls and options objects
    let requestOptions: any = { method: 'GET' };

    if (additionalOptions) {
      requestOptions = {
        ...requestOptions,
        params: options,
        ...additionalOptions,
      };
    } else if (options) {
      if (isRequestOptionsObject(options)) {
        // If options contains request option keys, treat it as full options object
        requestOptions = { ...requestOptions, ...options };
      } else {
        // Otherwise, treat the entire options as params (backward compatibility)
        requestOptions.params = options;
      }
    }

    // Log the params being sent for debugging
    if (requestOptions.params && endpoint.includes('analytics')) {
      console.log(`[API] GET ${endpoint} params:`, requestOptions.params);
      if (requestOptions.params.locationIds !== undefined) {
        console.log('[API] locationIds type:', typeof requestOptions.params.locationIds);
        console.log('[API] locationIds value:', requestOptions.params.locationIds);
        console.log('[API] locationIds length:', requestOptions.params.locationIds?.length);
      }
    }

    // Special logging for category-detail endpoint
    if (endpoint.includes('category-detail')) {
      console.log('[API] GET category-detail endpoint:', endpoint);
      console.log('[API] GET category-detail options received:', options);
      console.log('[API] GET category-detail requestOptions:', requestOptions);
      console.log('[API] GET category-detail requestOptions.params:', requestOptions.params);
      if (requestOptions.params?.filter_by_effective_sku !== undefined) {
        console.log(
          '[API] filter_by_effective_sku value:',
          requestOptions.params.filter_by_effective_sku,
        );
        console.log(
          '[API] filter_by_effective_sku type:',
          typeof requestOptions.params.filter_by_effective_sku,
        );
      }
    }

    return apiRequest<T>(endpoint, requestOptions);
  },

  post: <T = any>(endpoint: string, data?: any, options?: Record<string, unknown>) =>
    apiRequest<T>(endpoint, { method: 'POST', data, ...(options || {}) }),

  put: <T = any>(endpoint: string, data?: any, options?: Record<string, unknown>) =>
    apiRequest<T>(endpoint, { method: 'PUT', data, ...(options || {}) }),

  patch: <T = any>(endpoint: string, data?: any, options?: Record<string, unknown>) =>
    apiRequest<T>(endpoint, { method: 'PATCH', data, ...(options || {}) }),

  delete: <T = any>(endpoint: string, options?: Record<string, unknown>) =>
    apiRequest<T>(endpoint, { method: 'DELETE', ...(options || {}) }),
};

export default api;
